import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FEATURE_LABELS, money } from "@/pages/Premium";

const inp = "w-full bg-background border border-border rounded-xl px-4 py-2.5 outline-none focus:border-primary";
const EMPTY = { name: "", description: "", amount: 9.99, currency: "usd", days: 30, interval_label: "month", active: true,
  highlight: false, order: 0, features: { ad_free: true, downloads: true, premium_books: true }, perks: [] };

function L({ label, children }) {
  return <label className="block"><span className="track-label text-xs text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>;
}

function PlanForm({ plan, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...plan, features: { ...EMPTY.features, ...(plan.features || {}) } });
  const [perks, setPerks] = useState((plan.perks || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const u = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setBusy(true);
    const body = { ...f, amount: Number(f.amount), days: Number(f.days), order: Number(f.order) || 0,
      perks: perks.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (plan.id) await api.put(`/admin/premium/plans/${plan.id}`, body); else await api.post("/admin/premium/plans", body);
      toast.success("Plan saved"); onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="plan-form-modal">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-border bg-background p-6 space-y-4">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl font-bold">{plan.id ? "Edit plan" : "New plan"}</h3>
          <button onClick={onClose} data-testid="plan-form-close"><X className="h-5 w-5" /></button></div>
        <L label="Plan name"><input className={inp} value={f.name} onChange={u("name")} data-testid="plan-name" /></L>
        <L label="Short description"><input className={inp} value={f.description} onChange={u("description")} data-testid="plan-description" /></L>
        <div className="grid grid-cols-3 gap-3">
          <L label="Price"><input type="number" step="0.01" min="0.5" className={inp} value={f.amount} onChange={u("amount")} data-testid="plan-amount" /></L>
          <L label="Currency"><input className={inp} maxLength={3} value={f.currency} onChange={u("currency")} data-testid="plan-currency" /></L>
          <L label="Duration (days)"><input type="number" min="1" className={inp} value={f.days} onChange={u("days")} data-testid="plan-days" /></L>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Price label (/month, /year…)"><input className={inp} value={f.interval_label} onChange={u("interval_label")} data-testid="plan-interval" /></L>
          <L label="Display order"><input type="number" className={inp} value={f.order} onChange={u("order")} data-testid="plan-order" /></L>
        </div>
        <div>
          <p className="track-label text-xs text-muted-foreground mb-2">Access permissions</p>
          <div className="grid gap-2">
            {Object.entries(FEATURE_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.features[k] !== false} data-testid={`plan-feature-${k}`}
                  onChange={(e) => setF({ ...f, features: { ...f.features, [k]: e.target.checked } })} /> {label}
              </label>
            ))}
          </div>
        </div>
        <L label="Extra perks (one per line, display only)"><textarea rows={3} className={inp} value={perks} onChange={(e) => setPerks(e.target.value)} data-testid="plan-perks" /></L>
        <div className="flex gap-5 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={u("active")} data-testid="plan-active" /> Active (visible to readers)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.highlight} onChange={u("highlight")} data-testid="plan-highlight" /> "Best Value" badge</label>
        </div>
        <button disabled={busy} onClick={save} data-testid="plan-save-btn" className="w-full px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save plan"}</button>
      </div>
    </div>
  );
}

export function PremiumPlans() {
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/premium/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  const del = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? Existing members keep their access.`)) return;
    try { await api.delete(`/admin/premium/plans/${p.id}`); toast.success("Plan deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (p) => {
    try { await api.put(`/admin/premium/plans/${p.id}`, { ...p, active: !p.active }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="premium-plans-cms">
      <div className="flex items-center justify-between mb-5">
        <div><h2 className="font-display text-2xl font-bold">Premium plans</h2><p className="text-sm text-muted-foreground">Pricing, duration and access permissions — changes go live instantly.</p></div>
        <button onClick={() => setEdit({})} data-testid="plan-create-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Plus className="h-4 w-4" /> New plan</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`rounded-2xl border p-5 surface-2 ${p.active ? "border-border" : "border-dashed border-border opacity-60"}`} data-testid={`admin-plan-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><h3 className="font-semibold">{p.name}</h3><p className="text-2xl font-display font-bold mt-1">{money(p.amount, p.currency)}<span className="text-sm text-muted-foreground font-normal"> / {p.days} days</span></p></div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? "bg-primary/15 text-primary" : "bg-secondary"}`}>{p.active ? "Active" : "Inactive"}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{Object.entries(FEATURE_LABELS).filter(([k]) => p.features?.[k] !== false).map(([, l]) => l).join(" · ") || "No features"}</p>
            <p className="text-xs text-muted-foreground mt-1">{p.sales} paid sale(s)</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEdit(p)} data-testid={`plan-edit-${p.id}`} className="text-xs px-3 py-1.5 rounded-full border border-border inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
              <button onClick={() => toggle(p)} data-testid={`plan-toggle-${p.id}`} className="text-xs px-3 py-1.5 rounded-full border border-border">{p.active ? "Deactivate" : "Activate"}</button>
              <button onClick={() => del(p)} data-testid={`plan-delete-${p.id}`} className="text-xs px-3 py-1.5 rounded-full border border-border text-destructive inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      {!plans.length && <p className="text-muted-foreground text-center py-10">No plans yet — create one.</p>}
      {edit && <PlanForm plan={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

const STATUS_STYLE = { paid: "bg-primary/15 text-primary", pending: "bg-secondary", failed: "bg-destructive/15 text-destructive",
  expired: "bg-secondary text-muted-foreground", amount_mismatch: "bg-destructive/15 text-destructive" };

export function PaymentsPanel() {
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");
  const load = () => {
    api.get("/admin/payments").then((r) => setRows(r.data));
    api.get("/admin/payments/summary").then((r) => setSum(r.data));
  };
  useEffect(() => { load(); }, []);
  const reconcileAll = async () => {
    setBusy("all");
    try { const { data } = await api.post("/admin/payments/reconcile"); toast.success(`Checked ${data.checked} pending · ${data.updated} updated`); load(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  const reconcileOne = async (sid) => {
    setBusy(sid);
    try { const { data } = await api.post(`/admin/payments/${sid}/reconcile`); toast.success(`Status: ${data.payment_status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  const shown = filter === "all" ? rows : rows.filter((r) => r.payment_status === filter);
  return (
    <div data-testid="payments-panel">
      {sum && <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[["Revenue", Object.entries(sum.revenue).map(([c, v]) => money(v, c)).join(" + ") || money(0)], ["Paid", sum.paid], ["Pending", sum.pending], ["Failed / Expired", sum.failed + sum.expired], ["Premium users", sum.premium_users]].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-border surface-2 p-4" data-testid={`pay-sum-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}><p className="track-label text-xs text-muted-foreground">{k}</p><p className="text-xl font-display font-bold mt-1">{v}</p></div>
        ))}
      </div>}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "paid", "pending", "failed", "expired", "amount_mismatch"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`pay-filter-${s}`} className={`px-3 py-1.5 rounded-full text-xs capitalize ${filter === s ? "bg-primary text-primary-foreground" : "border border-border"}`}>{s.replace("_", " ")}</button>
        ))}
        <button onClick={reconcileAll} disabled={busy === "all"} data-testid="pay-reconcile-all" className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:border-primary disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${busy === "all" ? "animate-spin" : ""}`} /> Reconcile pending with Stripe
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground track-label text-xs border-b border-border">
            {["Customer", "Plan", "Amount", "Status", "Credited", "Date", ""].map((h) => <th key={h} className="py-3 px-2">{h}</th>)}
          </tr></thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.session_id} className="border-b border-border/50" data-testid={`pay-row-${p.session_id}`}>
                <td className="py-3 px-2">{p.user_email}</td><td className="px-2">{p.plan_name || p.plan}</td>
                <td className="px-2">{money(p.amount, p.currency || "usd")}</td>
                <td className="px-2"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[p.payment_status] || "bg-secondary"}`} title={p.note || ""}>{p.payment_status}</span></td>
                <td className="px-2">{p.credited || p.payment_status === "paid" ? "Yes" : "—"}</td>
                <td className="px-2 text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</td>
                <td className="px-2">{p.payment_status === "pending" && <button onClick={() => reconcileOne(p.session_id)} disabled={busy === p.session_id} data-testid={`pay-reconcile-${p.session_id}`} className="text-xs px-2 py-1 rounded border border-border">{busy === p.session_id ? "Checking…" : "Check"}</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <p className="text-muted-foreground text-center py-10">No transactions.</p>}
      </div>
    </div>
  );
}
